// THE STATEMENT'S TIE — the old PartyStatement's reconciliation (components/PartyStatement.tsx, Tie / tieNote), carried to
// the rebuilt Statement tab: per asset, three reads of the same money must agree —
//   end    the running balance the statement's last row reached (the route's rows, oldest first)
//   held   the statement route's own box, read the same exact way the running balance was summed
//   shown  the party read's balance, the figure the page shows above
// `ok` = end and held agree; `agrees` = held and shown agree. A window that stops before today reaches the balance on its
// closing day, not today's box, so no tie is claimed for it (the closing day is named instead).
import type { StatementRow } from '../../../lib/api';
import { sameAsset } from '../../../lib/naming';

/** Two reads of the same money agree to a millionth (PartyStatement AGREE_EPSILON: a fold across stablecoin codes is a
 *  float addition, its tail is far below anything settled on). */
export const AGREE_EPSILON = 1e-6;
const RECONCILE_DP = 2, RECONCILE_DP_MAX = 8;

export interface Tie { asset: string; end: number; held: number; shown: number; ok: boolean; agrees: boolean }

export function statementTies(rows: readonly StatementRow[], box: ReadonlyArray<{ asset: string; balance: number }>, balances: Record<string, number>): Tie[] {
  const ends = new Map<string, number>();
  for (const r of rows) ends.set(r.asset, r.running);
  return [...ends.entries()].map(([asset, end]) => {
    const held = box.find((b) => sameAsset(b.asset, asset))?.balance ?? 0;
    const shown = Object.entries(balances ?? {}).find(([code]) => sameAsset(code, asset))?.[1] ?? 0;
    return { asset, end, held, shown, ok: Math.abs(end - held) < AGREE_EPSILON, agrees: Math.abs(held - shown) < AGREE_EPSILON };
  });
}

/** The fewest decimals, from two up, at which two figures that do not agree print differently — a mismatch the operator
 *  is told about is one he can see. */
export function reconcileDp(a: number, b: number): number {
  for (let dp = RECONCILE_DP; dp < RECONCILE_DP_MAX; dp++) if (a.toFixed(dp) !== b.toFixed(dp)) return dp;
  return RECONCILE_DP_MAX;
}

/** Today's position is claimed only when the window reaches today. */
export function closesBeforeToday(to: string | undefined, today: string): boolean {
  return !!to && to < today;
}
